/**
 * PreviewPerformanceSection — raw stat values for the item, grouped by the
 * cube date-range. Lets the user see exactly which metrics drove the
 * optimizer's decision (and surface "no traffic found" hints loudly).
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useMemo } from 'react';

import { cn } from '@/lib/utils';

import { resolveDateRangeLabel } from '@/features/flow-optimizer/utils/date-range-label';

import { HoverSection } from './hover-primitives';
import {
  fmtStatValue,
  metricTone,
  orderMetrics,
  type OrderedMetric,
} from '../format';

export interface PreviewPerformanceSectionProps {
  metricsByDateRange?: Record<string, Record<string, number>>;
  /** Date ranges the user configured (from breakdown). Used when the cube has nothing. */
  expectedDateRanges?: ReadonlyArray<string>;
}

const PreviewPerformanceSection: FC<PreviewPerformanceSectionProps> = ({
  metricsByDateRange,
  expectedDateRanges = [],
}) => {
  const ranges = useMemo(
    () => Object.entries(metricsByDateRange ?? {}),
    [metricsByDateRange],
  );
  if (ranges.length === 0) {
    return <EmptyPerformance expectedDateRanges={expectedDateRanges} />;
  }
  return (
    <HoverSection label="Performance">
      <div className="space-y-2">
        {ranges.map(([dateRange, metrics]) => (
          <PerformanceCubeRow
            key={dateRange}
            dateRange={dateRange}
            ordered={orderMetrics(metrics)}
          />
        ))}
      </div>
    </HoverSection>
  );
};

const PerformanceCubeRow: FC<{
  dateRange: string;
  ordered: OrderedMetric[];
}> = ({ dateRange, ordered }) => (
  <div className="rounded border border-border/60">
    <div className="flex items-baseline justify-between gap-2 border-b border-border/60 px-2 py-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-foreground">
        {resolveDateRangeLabel(dateRange)}
      </span>
      <span className="text-[10px] tabular-nums text-muted-foreground">
        {ordered.length} stat{ordered.length === 1 ? '' : 's'}
      </span>
    </div>
    {ordered.length === 0 ? (
      <p className="px-2 py-1.5 text-[10px] italic text-muted-foreground">
        Cube returned no metric values for this item.
      </p>
    ) : (
      <ul className="grid grid-cols-2 gap-x-3 gap-y-0.5 px-2 py-1.5 text-[10px]">
        {ordered.map((m) => (
          <li
            key={m.id}
            className="flex items-baseline justify-between gap-2 tabular-nums"
          >
            <span className="truncate text-muted-foreground">{m.label}</span>
            <span className={cn('font-medium', metricTone(m.id, m.value))}>
              {fmtStatValue(m.id, m.value)}
            </span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const EmptyPerformance: FC<{ expectedDateRanges: ReadonlyArray<string> }> = ({
  expectedDateRanges,
}) => (
  <HoverSection label="Performance">
    <p className="rounded border border-amber-400/40 bg-amber-500/5 px-2 py-1.5 text-[10px] leading-snug text-amber-700 dark:text-amber-300">
      No stats found for this item
      {expectedDateRanges.length > 0 ? (
        <>
          {' '}
          in{' '}
          <strong className="font-semibold">
            {expectedDateRanges.map(resolveDateRangeLabel).join(', ')}
          </strong>
        </>
      ) : null}
      . Every formula input is 0, so the optimizer falls back to an equal split
      across siblings. Check the tracker date range, item id, or whether the
      report response actually contains this item.
    </p>
  </HoverSection>
);

export default PreviewPerformanceSection;
